import { useEffect, useState } from 'react'
import { api, type ResumenDeCatalogo } from '../servicios/api'
import { fechaContable } from '../fechas'

/**
 * Estado de los catálogos oficiales del SAT que usa la emisión (RF-1003).
 *
 * Es una pantalla de solo lectura, y existe por una razón concreta: hasta ahora el sistema tenía
 * catálogos sin decir de dónde salieron. Un catálogo del SAT no es un dato cualquiera —es contra
 * lo que se validan los comprobantes—, así que aquí se ve qué versión está cargada, de dónde
 * vino y, sobre todo, si se cotejó contra la publicación oficial o no.
 *
 * La carga es de la consola de plataforma: un despacho no debe poder reescribirle el catálogo
 * del SAT a todos los demás.
 */
export function CatalogosDelSat() {
  const [catalogos, setCatalogos] = useState<ResumenDeCatalogo[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.estadoDeCatalogos()
      .then(setCatalogos)
      .catch((e) => setError(e instanceof Error ? e.message : 'No se pudo consultar el estado de los catálogos.'))
  }, [])

  if (error) return <p className="error" role="alert">{error}</p>
  if (!catalogos) return <p>Cargando…</p>

  const faltantes = catalogos.filter((c) => c.estado === 'NoCargado')
  const sinCotejar = catalogos.filter((c) => c.estado === 'Cargado' && !c.cotejado)
  const parciales = catalogos.filter((c) => c.parcial)

  return (
    <section>
      <h1>Catálogos del SAT</h1>
      <p className="nota">
        Las claves contra las que se validan los comprobantes que emite la plataforma. Se
        actualizan sin desplegar código: una versión nueva del SAT es una carga, no una entrega.
      </p>

      {faltantes.length > 0 && (
        <p className="error" role="alert">
          <strong>{faltantes.length} catálogo(s) sin cargar.</strong> Mientras falten, no se puede
          capturar una clave de esos catálogos: {faltantes.map((c) => c.catalogo).join(', ')}.
        </p>
      )}

      {sinCotejar.length > 0 && (
        <p className="aviso" role="status">
          {sinCotejar.length} catálogo(s) cargados de <strong>fuente secundaria</strong>, sin
          cotejar contra la publicación oficial del SAT. Sirven para trabajar, pero una
          descripción o una clave podrían diferir.
        </p>
      )}

      {parciales.length > 0 && (
        <p className="aviso" role="status">
          {parciales.map((c) => c.catalogo).join(', ')} está(n) cargado(s) <strong>de forma
          parcial</strong>: tienen menos claves de las que publica el SAT. Si una clave legítima
          no aparece, es un hueco de la plataforma y no un error de captura.
        </p>
      )}

      <table className="tabla">
        <caption className="sutil">Estado de los nueve catálogos de emisión</caption>
        <thead>
          <tr>
            <th scope="col">Catálogo</th>
            <th scope="col">Estado</th>
            <th scope="col">Claves vigentes</th>
            <th scope="col">Versión</th>
            <th scope="col">Publicado</th>
            <th scope="col">Procedencia</th>
          </tr>
        </thead>
        <tbody>
          {catalogos.map((c) => (
            <tr key={c.catalogo}>
              <th scope="row" className="mono">{c.catalogo}</th>
              <td>
                <span className={`insignia ${c.estado === 'Cargado' ? 'vigente' : 'vencido'}`}>
                  {c.estado === 'Cargado' ? 'Cargado' : 'No cargado'}
                </span>
                {c.estado === 'Cargado' && !c.cotejado && (
                  <span className="insignia aviso" style={{ marginLeft: 'var(--space-2)' }}>Sin cotejar</span>
                )}
                {c.parcial && (
                  <span className="insignia aviso" style={{ marginLeft: 'var(--space-2)' }}>Parcial</span>
                )}
              </td>
              <td className="numero">{c.clavesVigentes.toLocaleString('es-MX')}</td>
              <td>{c.version ?? '—'}</td>
              <td>{c.publicadoPorSat ? fechaContable(c.publicadoPorSat) : '—'}</td>
              <td className="sutil">{c.origen ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {catalogos.some((c) => c.ultimaCargaError) && (
        <>
          <h2>Últimas cargas con problemas</h2>
          <ul className="lista-compacta">
            {catalogos.filter((c) => c.ultimaCargaError).map((c) => (
              <li key={c.catalogo}>
                <span className="mono">{c.catalogo}</span>
                <span className="secundaria">{c.ultimaCargaError}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}
